import { MessageBlueprint as ProtoMessageBlueprintModel } from '@stodge-inc/postscript-ts-protos/dist/postscript/messaging/common/v1/message_blueprint';
import type { MessageBlueprint } from 'components/rcsMessage/types';
import { AsyncJobStatus } from 'components/shopper/utils/asyncJobs';
import { api } from 'controllers/network/apiClient';
import { camelCaseKeysDeep } from 'utils/caseConversion';
import { SHOP_PROMPT_OVERRIDES_PATH } from './constants';
import {
  ConfigData,
  ContextResolverDefinitionsResponse,
  ExecuteContextResolverResponse,
  HandoffProvidersResponse,
  Response,
  ShopperDebugActivePromptsResponse,
  ShopperDebugAsyncStatusResponse,
  ShopperDebugAsyncStatusResponseRaw,
  ShopperProductsStatus,
  ShopperProductsStatusResponse,
  ShopperPromptOverridesResponse,
  ShopperReplyAgentBlueprintRaw,
  ShopperWebchatRequest,
  UpdateShopperPromptOverrides,
  UserPlaygroundAsyncResponse,
  UserPlaygroundRequest,
  UserPlaygroundStatusResponse,
} from './types';

export async function getShopConfigValues(): Promise<ConfigData> {
  return api.get('/v2/shopper/config');
}

export async function setShopConfigValues(
  data: Partial<ConfigData>,
): Promise<Response> {
  return api.post('/v2/shopper/config', data, { throwErrorAsString: false });
}

export async function setExcludedProductTags(
  tags: string[],
): Promise<Response> {
  return api.post(
    '/v2/shopper/config/excluded-product-tags',
    { excluded_product_tags: tags },
    { throwErrorAsString: false },
  );
}

export async function getShopperHandoffProviders(): Promise<HandoffProvidersResponse> {
  return api.get('/v2/shopper/handoff/providers');
}

export type ShopperDeactivationSource = 'responses' | 'automations';

export interface ShopperDeactivationFeedback {
  duration: 'temporary' | 'extended' | null;
  reason: string | null;
  skipped_feedback: boolean;
  source?: ShopperDeactivationSource;
}

export async function turnOffShopperAgent(
  feedback?: ShopperDeactivationFeedback,
): Promise<Response> {
  return api.post('/v2/shopper/agent/off', feedback);
}

/**
 * Post deactivation feedback to Slack without changing any shop state.
 *
 * Used when the deactivated tool (e.g. Shopper Automations) does NOT use the
 * `/v2/shopper/agent/off` endpoint and therefore can't piggy-back the feedback
 * on the deactivation call. The Responses path continues to send feedback in
 * the body of `/v2/shopper/agent/off` directly.
 */
export async function postShopperDeactivationFeedback(
  feedback: ShopperDeactivationFeedback,
): Promise<Response> {
  return api.post('/v2/shopper/feedback/deactivation', feedback);
}

export async function turnOnShopperAgent(): Promise<Response> {
  return api.post('/v2/shopper/agent/on');
}

export async function getShopperDebugActivePrompts(): Promise<ShopperDebugActivePromptsResponse> {
  const response = await api.get<ShopperDebugActivePromptsResponse>(
    '/v2/shopper/debug/active_prompts',
  );

  return response;
}

export async function getShopperProductsStatus(): Promise<ShopperProductsStatus> {
  const response = await api.get<ShopperProductsStatusResponse>(
    '/v2/shopper/products/status',
  );

  return {
    countProductsTotal: response.count_products_total,
    countProductsTruncated: response.count_products_truncated,
    countVariantsTruncated: response.count_variants_truncated,
    isTruncated: response.is_truncated,
    hasFullProductAccess: response.has_full_product_access,
  };
}

export async function getShopPromptOverrides(): Promise<ShopperPromptOverridesResponse | null> {
  const response = await api.get<ShopperPromptOverridesResponse>(
    SHOP_PROMPT_OVERRIDES_PATH,
  );

  return response ?? null;
}

export async function upsertShopPromptOverrides(
  data: UpdateShopperPromptOverrides,
): Promise<Response> {
  return api.post(SHOP_PROMPT_OVERRIDES_PATH, data);
}

export async function getContextResolverDefinitions(): Promise<ContextResolverDefinitionsResponse> {
  const response = await api.get<ContextResolverDefinitionsResponse>(
    '/v2/shopper/debug/prompt_context/resolver_definitions',
  );

  return response;
}

type ShopLabsFeatureValues<K extends string = string> = Record<K, boolean>;

interface ShopLabsFeatureValuesPayload<K extends string = string> {
  /* eslint-disable-next-line camelcase */
  shop_id: number;
  features: ShopLabsFeatureValues<K>;
}

export interface ShopLabsFeatureValuesResponse<K extends string = string> {
  shopId: number;
  features: ShopLabsFeatureValues<K>;
}

export async function getShopLabsFeatureValues<K extends string>(params: {
  shopId: number;
  keys: readonly K[];
}): Promise<ShopLabsFeatureValuesResponse<K>> {
  const { shopId, keys } = params;
  const query = [
    `shop_id=${encodeURIComponent(String(shopId))}`,
    ...keys.map((key) => `key=${encodeURIComponent(key)}`),
  ].join('&');
  const response = await api.get<ShopLabsFeatureValuesPayload<K>>(
    `/v2/shops/labs/feature-values?${query}`,
  );
  const { shop_id: responseShopId, features } = response;

  return {
    shopId: responseShopId,
    features,
  };
}

export async function postExecuteContextResolver(
  resolverName: string,
  payload: Record<string, unknown>,
): Promise<ExecuteContextResolverResponse> {
  const response = await api.post<ExecuteContextResolverResponse>(
    `/v2/shopper/debug/prompt_context/resolver/${resolverName}`,
    payload,
  );

  return response;
}

export async function sendShopperDebugMessageAsync(
  payload: ShopperWebchatRequest,
  // eslint-disable-next-line camelcase
): Promise<{ job_id: string; status: AsyncJobStatus }> {
  const response = await api.post(
    `/v2/shopper/debug/reply-agent/async`,
    payload,
  );
  return response;
}

function normalizeShopperReplyAgentBlueprint(
  blueprint?: ShopperReplyAgentBlueprintRaw,
): MessageBlueprint | undefined {
  if (!blueprint) {
    return undefined;
  }

  const camelCasedBlueprint = camelCaseKeysDeep(blueprint);
  const protoBlueprint =
    ProtoMessageBlueprintModel.fromJSON(camelCasedBlueprint);

  return ProtoMessageBlueprintModel.toJSON(protoBlueprint) as MessageBlueprint;
}

export async function getShopperDebugMessageAsyncStatus(
  jobId: string,
): Promise<ShopperDebugAsyncStatusResponse> {
  const response = await api.get<ShopperDebugAsyncStatusResponseRaw>(
    `/v2/shopper/debug/reply-agent/async/${jobId}`,
  );

  return {
    ...response,
    data: response.data
      ? {
          ...response.data,
          blueprint: normalizeShopperReplyAgentBlueprint(
            response.data.blueprint,
          ),
        }
      : null,
  };
}

export async function sendUserPlaygroundMessage(
  payload: UserPlaygroundRequest,
): Promise<UserPlaygroundAsyncResponse> {
  const response = await api.post<UserPlaygroundAsyncResponse>(
    '/v2/shopper/playground/message',
    payload,
  );
  return response;
}

export async function getUserPlaygroundMessageStatus(
  jobId: string,
): Promise<UserPlaygroundStatusResponse> {
  const response = await api.get<UserPlaygroundStatusResponse>(
    `/v2/shopper/playground/message/${jobId}`,
  );
  return response;
}
